<template>
  <public-layout>

    <!-- ═══════ HERO ═══════ -->
    <div class="blog-hero">
      <div class="blog-hero__dots"></div>
      <div class="sc-container blog-hero__inner">
        <a href="/blog" class="blog-hero__back">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Blog
        </a>
        <span v-if="details.category" class="blog-hero__cat">{{ details.category.name }}</span>
        <h1 class="blog-hero__title">{{ details.title }}</h1>
        <div class="blog-hero__meta">
          <span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            {{ formatDate2(details.created_at) }}
          </span>
          <span v-if="details.is_user && details.user">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <a
              :href="details.user.type === 'company' ? '/company/' + details.user.slug : '/sc/profile/' + details.user.slug"
              class="blog-hero__author-link"
            >{{ details.user.name }}</a>
          </span>
          <span v-else>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Admin
          </span>
          <span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            {{ details.comments ? details.comments.length : 0 }} comments
          </span>
          <span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            {{ details.views || 0 }} views
          </span>
        </div>
        <button v-if="id && String(id) === String(details.user_id)" class="blog-hero__edit" @click="openUpdateBlog">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          Edit Post
        </button>
      </div>
    </div>

    <!-- ═══════ BODY ═══════ -->
    <div class="sc-container blog-layout">

      <!-- ── Main ── -->
      <main class="blog-main">

        <!-- Hero image -->
        <div v-if="heroImage" class="blog-cover">
          <img :src="heroImage" :alt="details.title" @error="onImgError" />
        </div>

        <!-- Article body -->
        <article class="blog-article" v-html="details.description"></article>

        <!-- Video embeds -->
        <div v-if="youtubeVideoId" class="blog-video-section">
          <h3 class="blog-video-section__title">Video</h3>
          <div class="sc-video-wrap">
            <iframe
              :src="'https://www.youtube.com/embed/' + youtubeVideoId"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              class="sc-video-frame"
            ></iframe>
          </div>
        </div>
        <div v-if="instagramShortcode" class="blog-video-section">
          <h3 class="blog-video-section__title">Instagram</h3>
          <div class="sc-video-wrap sc-video-wrap--instagram">
            <iframe
              :src="'https://www.instagram.com/p/' + instagramShortcode + '/embed'"
              frameborder="0"
              scrolling="no"
              allowtransparency="true"
              class="sc-video-frame"
            ></iframe>
          </div>
        </div>

        <!-- Share -->
        <div v-if="details.slug" class="blog-share">
          <span class="blog-share__label">Share</span>
          <a :href="'https://www.facebook.com/sharer/sharer.php?u=' + pageUrl" target="_blank" rel="noopener" class="share-pill facebook">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
            Facebook
          </a>
          <a :href="'https://twitter.com/intent/tweet?url=' + pageUrl + '&text=' + encodeURIComponent(details.title)" target="_blank" rel="noopener" class="share-pill twitter">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            Twitter / X
          </a>
          <a :href="'https://www.linkedin.com/shareArticle?mini=true&url=' + pageUrl" target="_blank" rel="noopener" class="share-pill linkedin">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
            LinkedIn
          </a>
        </div>

        <!-- Comments -->
        <section class="blog-comments">
          <h3 class="blog-section-title">
            {{ details.comments ? details.comments.length : 0 }} Comment{{ details.comments && details.comments.length !== 1 ? 's' : '' }}
          </h3>

          <div v-if="details.comments && details.comments.length" class="comments-list">
            <div v-for="comment in details.comments" :key="comment.id" class="comment">
              <img
                :src="comment.user && comment.user.avatar ? $avatarSrc(comment.user.avatar, 'avatar') : '/assets/avatar.png'"
                class="comment__avatar"
                alt=""
                @error="onImgError"
              />
              <div class="comment__body">
                <div class="comment__header">
                  <span class="comment__name">{{ comment.user ? comment.user.name : 'Anonymous' }}</span>
                  <span class="comment__date">{{ formatDate2(comment.created_at) }}</span>
                  <button v-if="id" class="comment__reply-btn" @click="replyComment(comment.id)">Reply</button>
                </div>
                <p class="comment__text">{{ comment.comment }}</p>

                <!-- Sub-comments -->
                <div v-if="comment.sub && comment.sub.length" class="comment-replies">
                  <div v-for="sub in comment.sub" :key="sub.id" class="comment comment--reply">
                    <img
                      :src="sub.user && sub.user.avatar ? $avatarSrc(sub.user.avatar, 'avatar') : '/assets/avatar.png'"
                      class="comment__avatar comment__avatar--sm"
                      alt=""
                      @error="onImgError"
                    />
                    <div class="comment__body">
                      <div class="comment__header">
                        <span class="comment__name">{{ sub.user ? sub.user.name : 'Anonymous' }}</span>
                        <span class="comment__date">{{ formatDate2(sub.created_at) }}</span>
                      </div>
                      <p class="comment__text">{{ sub.comment }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="comments-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            <p>No comments yet. Be the first to comment!</p>
          </div>
        </section>

        <!-- Leave a comment (logged in) -->
        <section v-if="id" class="blog-comment-form">
          <h3 class="blog-section-title">Leave a Comment</h3>
          <form id="comment-form" @submit.prevent="submitComment">
            <textarea name="comment" rows="5" placeholder="Write your comment..." class="bd-textarea" required></textarea>
            <input type="hidden" name="blog_id" :value="details.id" />
            <button type="submit" class="btn-primary bd-submit" :disabled="loading">
              <span v-if="loading" class="bd-spinner"></span>
              <span v-else>Post Comment</span>
            </button>
          </form>
        </section>

        <!-- Sign-in prompt (guest) -->
        <section v-else class="blog-signin-prompt">
          <h3 class="blog-section-title">Join the Discussion</h3>
          <p class="blog-signin-prompt__text">Sign in to leave a comment.</p>
          <form id="login-here" @submit.prevent="LoginHere" class="bd-login-form">
            <div class="bd-field">
              <label>Email</label>
              <input type="email" name="email" placeholder="you@example.com" required />
            </div>
            <div class="bd-field">
              <label>Password</label>
              <input type="password" name="password" placeholder="Your password" required />
            </div>
            <button type="submit" class="btn-primary bd-submit" :disabled="loading">
              <span v-if="loading" class="bd-spinner"></span>
              <span v-else>Sign in &amp; comment</span>
            </button>
            <p class="bd-login-link">Don't have an account? <a href="/register">Sign up free</a></p>
          </form>
        </section>

      </main>

      <!-- ── Sidebar ── -->
      <aside class="blog-sidebar">

        <!-- Search -->
        <div class="sb-card">
          <h4 class="sb-title">Search</h4>
          <form action="/blog-search" class="sb-search">
            <input name="search" type="text" placeholder="Search articles..." />
            <button type="submit">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </button>
          </form>
        </div>

        <!-- Featured posts -->
        <div class="sb-card" v-if="featured.length">
          <h4 class="sb-title">Featured Posts</h4>
          <div class="sb-posts">
            <a v-for="(post, i) in featured" :key="i" :href="'/blog/' + post.slug" class="sb-post">
              <span class="sb-post__title">{{ post.title }}</span>
              <span class="sb-post__date">{{ formatDate2(post.created_at) }}</span>
            </a>
          </div>
        </div>

        <!-- Categories -->
        <div class="sb-card" v-if="categories.length">
          <h4 class="sb-title">Categories</h4>
          <div class="sb-cats">
            <a v-for="(cat, i) in categories" :key="i" :href="'/blog/category/' + cat.slug" class="sb-cat">
              {{ cat.name }}
            </a>
          </div>
        </div>

        <!-- Adverts -->
        <div v-if="adverts.length" class="sb-card sb-adverts">
          <a v-for="ad in adverts.slice(0, 2)" :key="ad.id" :href="ad.link" target="_blank" rel="noopener" class="sb-advert">
            <img :src="image_url + 'advert/' + ad.image" :alt="ad.title || 'Advertisement'" />
          </a>
        </div>

      </aside>
    </div>

    <!-- ═══════ REPLY MODAL ═══════ -->
    <div v-if="showReplyModal" class="bd-modal-overlay" @click.self="closeReplyCommentModal">
      <div class="bd-modal">
        <div class="bd-modal__header">
          <h3>Leave a Reply</h3>
          <button class="bd-modal__close" @click="closeReplyCommentModal">&times;</button>
        </div>
        <div class="bd-modal__body">
          <form id="reply-form" @submit.prevent="submitReplyComment">
            <textarea name="comment" rows="5" placeholder="Write your reply..." class="bd-textarea" required></textarea>
            <input type="hidden" name="blog_id" :value="details.id" />
            <input type="hidden" name="parent_id" :value="parent_id" />
            <button type="submit" class="btn-primary bd-submit" :disabled="loading">
              <span v-if="loading" class="bd-spinner"></span>
              <span v-else>Post Reply</span>
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- ═══════ EDIT MODAL ═══════ -->
    <div v-if="showEditModal" class="bd-modal-overlay" @click.self="closeUpdateModal">
      <div class="bd-modal bd-modal--wide">
        <div class="bd-modal__header">
          <h3>Edit Post</h3>
          <button class="bd-modal__close" @click="closeUpdateModal">&times;</button>
        </div>
        <div class="bd-modal__body">
          <div class="bd-field">
            <label>Blog Title</label>
            <input type="text" v-model="details.title" />
          </div>
          <div class="bd-field">
            <label>Category</label>
            <select v-model="details.cat_id">
              <option value="" disabled>Select Category</option>
              <option v-for="(cat, i) in categories" :key="i" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>
          <div class="bd-field">
            <label>Description</label>
            <QuillEditor v-model:content="details.description" contentType="html" :toolbar="customToolbar" style="min-height:200px;" />
          </div>
          <div class="bd-field">
            <label>Images</label>
            <div class="bd-images-row">
              <div v-for="img in details.images" :key="img.id" class="bd-img-thumb">
                <img :src="img.path" alt="" />
                <button type="button" class="bd-img-remove" @click="deleteBlogImage(img.id)">&times;</button>
              </div>
            </div>
            <vue-upload-multiple-image
              @upload-success="uploadImage"
              @before-remove="beforeRemove"
              @edit-image="editImage"
              @data-change="dataChange"
              :data-images="images"
              dragText="Drag images here"
              browseText="Browse"
              :maxImage="3"
            />
          </div>
        </div>
        <div class="bd-modal__footer">
          <button class="btn-outline" @click="closeUpdateModal">Cancel</button>
          <button class="btn-primary" @click="updateBlog" :disabled="loading">
            <span v-if="loading" class="bd-spinner"></span>
            <span v-else>Save Changes</span>
          </button>
        </div>
      </div>
    </div>

  </public-layout>
</template>

<script>
import PublicLayout from '@/layouts/PublicLayout.vue';
import axios from 'axios';
import { QuillEditor } from '@vueup/vue-quill';
import { useSEO, setJsonLd, SITE_ORIGIN } from '@/utils/seo';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import moment from 'moment';
import VueUploadMultipleImage from '@/components/VueUploadMultipleImage';

export default {
  name: 'BlogDetail',
  components: { PublicLayout, QuillEditor, VueUploadMultipleImage },

  data() {
    return {
      loading: false,
      id: localStorage.getItem('user'),
      details: {},
      featured: [],
      categories: [],
      adverts: [],
      images: [],
      parent_id: '',
      showReplyModal: false,
      showEditModal: false,
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ],
    };
  },

  computed: {
    image_url() { return process.env.VUE_APP_IMAGE_URL || ''; },
    frontend_url() { return process.env.VUE_APP_FRONTEND_URL || '/'; },
    pageUrl() { return encodeURIComponent(window.location.href); },
    heroImage() {
      if (!this.details) return '';
      if (this.details.image) return this.details.image;
      if (this.details.images && this.details.images.length) return this.details.images[0].path;
      return '';
    },
    youtubeVideoId() {
      const url = this.details && this.details.youtube_url;
      if (!url) return '';
      const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
      return m ? m[1] : '';
    },
    instagramShortcode() {
      const url = this.details && this.details.instagram_url;
      if (!url) return '';
      const m = url.match(/instagram\.com\/(?:p|reel)\/([A-Za-z0-9_-]+)/);
      return m ? m[1] : '';
    },
  },


  created() {
    this.fetchData();
    this.fetchFeaturedBlog();
    this.fetchAdverts();
    this.fetchCategories();
    this.updateViewCount();
  },

  methods: {
    updateViewCount() {
      axios.post(process.env.VUE_APP_API_URL + 'blog/update-view-count', {
        user_id: localStorage.getItem('user'),
        slug: this.$route.params.id,
      }, { skipToast: true }).catch(() => {});
    },

    fetchData() {
      axios(process.env.VUE_APP_API_URL + 'blog/detail/' + this.$route.params.id)
        .then(res => {
          if (res.status === 200) {
            this.details = res.data;
            const blogUrl = SITE_ORIGIN + '/blog/' + this.details.slug;
            useSEO({
              title: this.details.title,
              description: this.details.description,
              image: this.heroImage,
              url: blogUrl,
              type: 'article',
            });
            setJsonLd({
              '@context': 'https://schema.org',
              '@type': 'BlogPosting',
              headline: this.details.title,
              description: (this.details.description || '').replace(/<[^>]*>/g, '').slice(0, 300),
              image: this.heroImage || undefined,
              url: blogUrl,
              datePublished: this.details.created_at,
              dateModified: this.details.updated_at || this.details.created_at,
              author: this.details.user ? {
                '@type': 'Person',
                name: this.details.user.name,
                url: SITE_ORIGIN + '/sc/profile/' + this.details.user.slug,
              } : undefined,
              publisher: {
                '@type': 'Organization',
                name: 'Structurecity',
                logo: { '@type': 'ImageObject', url: SITE_ORIGIN + '/assets/favicon.png' },
              },
            });
          }
        })
        .catch(() => {});
    },

    fetchFeaturedBlog() {
      axios(process.env.VUE_APP_API_URL + 'blog/featured')
        .then(res => { if (res.status === 200) this.featured = res.data; })
        .catch(() => {});
    },

    fetchAdverts() {
      axios(process.env.VUE_APP_API_URL + 'adverts')
        .then(res => { if (res.status === 200) this.adverts = res.data; })
        .catch(() => {});
    },

    fetchCategories() {
      axios(process.env.VUE_APP_API_URL + 'blog/categories')
        .then(res => { if (res.status === 200) this.categories = res.data; })
        .catch(() => {});
    },

    submitComment() {
      this.loading = true;
      const form = document.getElementById('comment-form');
      const data = new FormData(form);
      data.append('user_id', this.id);
      axios.post(process.env.VUE_APP_API_URL + 'blog/comment', data)
        .then(() => { form.reset(); this.fetchData(); })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    LoginHere() {
      this.loading = true;
      const form = document.getElementById('login-here');
      const data = new FormData(form);
      axios.post(process.env.VUE_APP_API_URL + 'auth/login', data)
        .then(res => {
          if (res.status === 200) {
            localStorage.setItem('user', res.data.id);
            localStorage.setItem('slug', res.data.slug);
            this.$router.go();
          }
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    replyComment(id) {
      this.parent_id = id;
      this.showReplyModal = true;
    },

    submitReplyComment() {
      this.loading = true;
      const form = document.getElementById('reply-form');
      const data = new FormData(form);
      data.append('user_id', this.id);
      axios.post(process.env.VUE_APP_API_URL + 'blog/comment', data)
        .then(() => { form.reset(); this.showReplyModal = false; this.fetchData(); })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    closeReplyCommentModal() { this.showReplyModal = false; },

    updateBlog() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'blog/update-blog', this.details)
        .then(() => { this.images = []; this.closeUpdateModal(); this.fetchData(); })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    deleteBlogImage(id) {
      axios(process.env.VUE_APP_API_URL + 'blog/delete-blog-image/' + id)
        .then(() => { this.fetchData(); })
        .catch(() => {});
    },

    openUpdateBlog() { this.showEditModal = true; },
    closeUpdateModal() { this.showEditModal = false; },

    uploadImage(formData, index, fileList) { this.details.update_images = fileList; },
    beforeRemove(index, done) { if (confirm('Remove image?')) done(); },
    editImage() {},
    dataChange() {},

    formatDate2(date) { return moment(date).format('MMMM DD, YYYY'); },
    onImgError(e) { e.target.src = '/assets/avatar.png'; },
  },
};
</script>

<style scoped>
/* ── Hero ──────────────────────────────────────────────── */
.blog-hero {
  position: relative; overflow: hidden;
  background: linear-gradient(135deg, #0a1628 0%, #1e3a5f 55%, #1a4a7a 100%);
  padding: 7rem 1.5rem 3.5rem;
  margin-top: 68px;
}
.blog-hero__dots {
  position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(rgba(255,255,255,.06) 1px, transparent 1px);
  background-size: 36px 36px;
}
.blog-hero__inner { position: relative; z-index: 2; max-width: 860px; }
.blog-hero__back {
  display: inline-flex; align-items: center; gap: .375rem;
  color: rgba(255,255,255,.6); font-size: .875rem; font-weight: 500;
  text-decoration: none; margin-bottom: 1.5rem;
  transition: color .15s;
}
.blog-hero__back:hover { color: #fff; text-decoration: none; }
.blog-hero__cat {
  display: inline-block; padding: .3rem .875rem;
  background: rgba(245,158,11,.15); border: 1px solid rgba(245,158,11,.35);
  border-radius: var(--radius-full); color: var(--accent);
  font-size: .8rem; font-weight: 600; letter-spacing: .05em; text-transform: uppercase;
  margin-bottom: 1rem;
}
.blog-hero__title {
  font-size: clamp(1.75rem, 4vw, 2.75rem); font-weight: 800; color: #fff;
  line-height: 1.2; letter-spacing: -.03em; margin: 0 0 1.25rem;
}
.blog-hero__meta {
  display: flex; align-items: center; flex-wrap: wrap; gap: 1.25rem;
  font-size: .875rem; color: rgba(255,255,255,.6);
}
.blog-hero__meta span { display: flex; align-items: center; gap: .375rem; }
.blog-hero__author-link { color: rgba(255,255,255,.85); text-decoration: none; font-weight: 500; }
.blog-hero__author-link:hover { color: #fff; text-decoration: underline; }
.blog-hero__edit {
  display: inline-flex; align-items: center; gap: .5rem;
  margin-top: 1.5rem; padding: .5rem 1.25rem;
  background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.2);
  border-radius: var(--radius-full); color: #fff; font-size: .875rem; font-weight: 500;
  cursor: pointer; transition: background .15s;
}
.blog-hero__edit:hover { background: rgba(255,255,255,.18); }

/* ── Body layout ───────────────────────────────────────── */
.blog-layout {
  display: grid; grid-template-columns: 1fr 300px; gap: 2.5rem;
  padding: 3rem 1.5rem 5rem; align-items: start;
}
.blog-main { min-width: 0; }

/* ── Cover image ───────────────────────────────────────── */
.blog-cover {
  border-radius: var(--radius-lg); overflow: hidden;
  margin-bottom: 2.5rem; max-height: 480px;
}
.blog-cover img { width: 100%; max-height: 480px; object-fit: cover; display: block; }

/* ── Article ───────────────────────────────────────────── */
.blog-article {
  font-size: 1.0625rem; line-height: 1.8; color: var(--text-muted);
  margin-bottom: 2.5rem;
}
.blog-article :deep(h1),
.blog-article :deep(h2),
.blog-article :deep(h3) { color: var(--text); font-weight: 700; margin: 1.75rem 0 .75rem; line-height: 1.3; }
.blog-article :deep(p)  { margin: 0 0 1rem; }
.blog-article :deep(ul),
.blog-article :deep(ol) { padding-left: 1.5rem; margin: 0 0 1rem; }
.blog-article :deep(li) { margin-bottom: .375rem; }
.blog-article :deep(strong) { color: var(--text); font-weight: 600; }
.blog-article :deep(a) { color: var(--primary); }
.blog-article :deep(blockquote) {
  border-left: 3px solid var(--primary); padding-left: 1.25rem;
  margin: 1.5rem 0; color: var(--text-muted); font-style: italic;
}
.blog-article :deep(img) { max-width: 100%; border-radius: var(--radius); margin: 1rem 0; }

/* ── Share ─────────────────────────────────────────────── */
.blog-share {
  display: flex; align-items: center; flex-wrap: wrap; gap: .625rem;
  padding: 1.5rem 0; border-top: 1px solid var(--border-light); border-bottom: 1px solid var(--border-light);
  margin-bottom: 2.5rem;
}
.blog-share__label { font-size: .875rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: .06em; margin-right: .25rem; }
.share-pill {
  display: inline-flex; align-items: center; gap: .375rem;
  padding: .4rem 1rem; border-radius: var(--radius-full);
  font-size: .8125rem; font-weight: 600; text-decoration: none;
  transition: opacity .15s;
}
.share-pill:hover { opacity: .85; text-decoration: none; }
.share-pill.facebook  { background: #1877f2; color: #fff; }
.share-pill.twitter   { background: #000; color: #fff; }
.share-pill.linkedin  { background: #0a66c2; color: #fff; }

/* ── Section titles ────────────────────────────────────── */
.blog-section-title {
  font-size: 1.125rem; font-weight: 700; color: var(--text);
  margin: 0 0 1.5rem; padding-bottom: .75rem; border-bottom: 2px solid var(--primary);
  display: inline-block;
}

/* ── Comments ──────────────────────────────────────────── */
.blog-comments { margin-bottom: 2.5rem; }
.comments-list { display: flex; flex-direction: column; gap: 1.5rem; }
.comment { display: flex; gap: 1rem; }
.comment--reply { margin-top: 1rem; }
.comment-replies { margin-top: 1rem; padding-left: 1rem; border-left: 2px solid var(--border-light); }
.comment__avatar {
  width: 44px; height: 44px; border-radius: 50%; object-fit: cover; flex-shrink: 0;
  border: 2px solid var(--border-light);
}
.comment__avatar--sm { width: 36px; height: 36px; }
.comment__body { flex: 1; min-width: 0; }
.comment__header { display: flex; align-items: center; flex-wrap: wrap; gap: .625rem; margin-bottom: .375rem; }
.comment__name { font-weight: 700; font-size: .9375rem; color: var(--text); }
.comment__date { font-size: .8125rem; color: var(--text-muted); }
.comment__reply-btn {
  margin-left: auto; background: none; border: none; padding: .2rem .625rem;
  font-size: .8125rem; font-weight: 600; color: var(--primary); cursor: pointer;
  border-radius: var(--radius-full); transition: background .15s;
}
.comment__reply-btn:hover { background: rgba(30,58,95,.08); }
.comment__text { font-size: .9375rem; color: var(--text-muted); line-height: 1.6; margin: 0; }
.comments-empty {
  display: flex; flex-direction: column; align-items: center; gap: .75rem;
  padding: 2.5rem 1rem; color: var(--text-muted); text-align: center; opacity: .5;
}
.comments-empty p { margin: 0; font-size: .9375rem; }

/* ── Comment / sign-in forms ───────────────────────────── */
.blog-comment-form,
.blog-signin-prompt { margin-bottom: 2rem; }
.blog-signin-prompt__text { color: var(--text-muted); margin: 0 0 1.25rem; font-size: .9375rem; }
.bd-textarea {
  width: 100%; min-height: 120px; padding: .875rem 1rem;
  border: 1.5px solid var(--border); border-radius: var(--radius);
  font-family: var(--font); font-size: .9375rem; color: var(--text);
  resize: vertical; outline: none; transition: border-color .15s;
  display: block; margin-bottom: 1rem;
}
.bd-textarea:focus { border-color: var(--primary); }
.bd-login-form { max-width: 420px; }
.bd-field { margin-bottom: 1rem; }
.bd-field label { display: block; font-size: .875rem; font-weight: 500; margin-bottom: .375rem; color: var(--text); }
.bd-field input,
.bd-field select {
  width: 100%; padding: .625rem .875rem; border: 1.5px solid var(--border);
  border-radius: var(--radius-sm); font-family: var(--font); font-size: .9375rem;
  color: var(--text); background: var(--surface); outline: none;
  transition: border-color .15s;
}
.bd-field input:focus,
.bd-field select:focus { border-color: var(--primary); }
.bd-submit { display: inline-flex; align-items: center; gap: .5rem; padding: .75rem 1.75rem; font-size: .9375rem; cursor: pointer; }
.bd-login-link { margin-top: 1rem; font-size: .875rem; color: var(--text-muted); }
.bd-login-link a { color: var(--primary); font-weight: 600; text-decoration: none; }
.bd-spinner {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  border-radius: 50%; animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Sidebar ───────────────────────────────────────────── */
.blog-sidebar { position: sticky; top: 88px; display: flex; flex-direction: column; gap: 1.25rem; }
.sb-card {
  background: var(--surface); border: 1px solid var(--border-light);
  border-radius: var(--radius-lg); padding: 1.25rem; box-shadow: var(--shadow-sm);
}
.sb-title {
  font-size: .8125rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: .07em; color: var(--text-muted); margin: 0 0 1rem;
}
.sb-search { display: flex; gap: .5rem; }
.sb-search input {
  flex: 1; padding: .6rem .875rem; border: 1.5px solid var(--border);
  border-radius: var(--radius-sm); font-family: var(--font); font-size: .875rem;
  color: var(--text); outline: none; transition: border-color .15s;
}
.sb-search input:focus { border-color: var(--primary); }
.sb-search button {
  padding: .6rem .875rem; background: var(--primary); color: #fff;
  border: none; border-radius: var(--radius-sm); cursor: pointer; display: flex; align-items: center;
  transition: background .15s;
}
.sb-search button:hover { background: var(--primary-dark); }
.sb-posts { display: flex; flex-direction: column; gap: .75rem; }
.sb-post {
  display: flex; flex-direction: column; gap: .2rem;
  text-decoration: none; padding: .625rem .75rem;
  border-radius: var(--radius-sm); transition: background .15s;
}
.sb-post:hover { background: var(--surface-3); text-decoration: none; }
.sb-post__title { font-size: .875rem; font-weight: 600; color: var(--text); line-height: 1.35; }
.sb-post__date  { font-size: .775rem; color: var(--text-muted); }
.sb-cats { display: flex; flex-wrap: wrap; gap: .5rem; }
.sb-cat {
  padding: .3rem .875rem; border-radius: var(--radius-full);
  background: var(--surface-3); font-size: .8125rem; font-weight: 500;
  color: var(--text-muted); text-decoration: none; transition: background .15s, color .15s;
}
.sb-cat:hover { background: var(--primary); color: #fff; text-decoration: none; }
.sb-adverts { display: flex; flex-direction: column; gap: .75rem; padding: .75rem; }
.sb-advert img { width: 100%; border-radius: var(--radius); display: block; }

/* ── Modals ────────────────────────────────────────────── */
.bd-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 1rem;
}
.bd-modal {
  background: var(--surface); border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl); width: 100%; max-width: 480px;
  max-height: 90vh; overflow-y: auto;
}
.bd-modal--wide { max-width: 720px; }
.bd-modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.5rem 1.75rem 0;
}
.bd-modal__header h3 { font-size: 1.125rem; font-weight: 700; color: var(--text); margin: 0; }
.bd-modal__close {
  background: none; border: none; font-size: 1.5rem;
  color: var(--text-muted); cursor: pointer; line-height: 1; padding: .25rem;
}
.bd-modal__body { padding: 1.25rem 1.75rem; }
.bd-modal__footer {
  display: flex; justify-content: flex-end; gap: .75rem;
  padding: 1rem 1.75rem 1.5rem;
  border-top: 1px solid var(--border-light);
}
.bd-images-row { display: flex; flex-wrap: wrap; gap: .625rem; margin-bottom: .75rem; }
.bd-img-thumb { position: relative; width: 64px; height: 64px; border-radius: var(--radius-sm); overflow: hidden; }
.bd-img-thumb img { width: 100%; height: 100%; object-fit: cover; }
.bd-img-remove {
  position: absolute; top: 2px; right: 2px; background: rgba(220,38,38,.85); color: #fff;
  border: none; border-radius: 50%; width: 18px; height: 18px; font-size: .75rem;
  cursor: pointer; display: flex; align-items: center; justify-content: center; line-height: 1;
}

/* ── Responsive ────────────────────────────────────────── */
@media (max-width: 900px) {
  .blog-layout { grid-template-columns: 1fr; }
  .blog-sidebar { position: static; }
}
@media (max-width: 600px) {
  .blog-hero { padding: 6rem 1rem 2.5rem; }
  .blog-layout { padding: 2rem 1rem 3rem; }
}

.blog-video-section { margin: 2rem 0; }
.blog-video-section__title { font-size: 1.125rem; font-weight: 700; margin-bottom: 1rem; color: var(--text); }
.sc-video-wrap { position: relative; width: 100%; padding-top: 56.25%; border-radius: var(--radius-lg, 12px); overflow: hidden; background: #000; }
.sc-video-wrap--instagram { padding-top: 130%; max-width: 540px; background: #fafafa; }
.sc-video-frame { position: absolute; inset: 0; width: 100%; height: 100%; border: none; }
</style>
